type ChecklistLoaderProps = {
  /**
   * `screen` dims the entire viewport (used before the app chrome exists).
   * `container` fills the nearest positioned ancestor instead — the main
   * content panel — so the header and sidebar stay live and undimmed.
   */
  scope?: 'screen' | 'container'
}

export default function ChecklistLoader({ scope = 'screen' }: ChecklistLoaderProps) {
  const contained = scope === 'container'
  return (
    <div
      data-checklist-overlay={scope}
      className={`${contained ? 'absolute' : 'fixed'} inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm`}
      style={{ background: contained ? 'hsl(var(--background) / 0.6)' : 'rgba(0,0,0,0.4)' }}
    >
      <div className="checklist-loader" role="status" aria-label="Loading">
        <div className="cl-row r0">
          <span className="cl-bwrap">
            <span className="cl-ripple" aria-hidden="true" />
            <span className="cl-box">
              <svg className="cl-chk" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 12.5l3.8 3.8L18.5 8" />
              </svg>
            </span>
          </span>
          <span className="cl-bar" style={{ width: '78%' }}>
            <span className="cl-shim s0" />
          </span>
        </div>
        <div className="cl-row r1">
          <span className="cl-bwrap">
            <span className="cl-ripple" aria-hidden="true" />
            <span className="cl-box">
              <svg className="cl-chk" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 12.5l3.8 3.8L18.5 8" />
              </svg>
            </span>
          </span>
          <span className="cl-bar" style={{ width: '60%' }}>
            <span className="cl-shim s1" />
          </span>
        </div>
        <div className="cl-row r2">
          <span className="cl-bwrap">
            <span className="cl-ripple" aria-hidden="true" />
            <span className="cl-box">
              <svg className="cl-chk" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 12.5l3.8 3.8L18.5 8" />
              </svg>
            </span>
          </span>
          <span className="cl-bar" style={{ width: '90%' }}>
            <span className="cl-shim s2" />
          </span>
        </div>
      </div>

      <style>{`
        .checklist-loader {
          --cl-card-bg:      hsl(var(--card));
          --cl-bar-bg:       hsl(var(--muted));
          --cl-box-border:   hsl(var(--muted-foreground) / 0.6);
          --cl-accent:       #84c341;
          --cl-check:        #ffffff;
          --cl-shimmer:      hsl(var(--foreground) / 0.08);
          --cl-cycle:        3.4s;
          --cl-shimmer-time: 1.7s;

          width: 330px;
          box-sizing: border-box;
          background: var(--cl-card-bg);
          border-radius: 16px;
          border: 1px solid hsl(var(--border));
          box-shadow: 0 10px 30px -12px hsl(var(--foreground) / 0.25);
          padding: 1.4rem 1.5rem;
        }
        .checklist-loader .cl-row {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        .checklist-loader .cl-row:last-child { margin-bottom: 0; }
        .checklist-loader .cl-bwrap {
          position: relative;
          width: 28px;
          height: 28px;
          flex: none;
        }
        .checklist-loader .cl-ripple {
          position: absolute;
          inset: 0;
          z-index: 0;
          border-radius: 8px;
          background: var(--cl-accent);
          opacity: 0;
          transform: scale(0.6);
        }
        .checklist-loader .cl-box {
          position: absolute;
          inset: 0;
          z-index: 1;
          border-radius: 8px;
          border: 1.5px solid var(--cl-box-border);
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .checklist-loader .cl-chk { width: 17px; height: 17px; }
        .checklist-loader .cl-chk path {
          fill: none;
          stroke: var(--cl-check);
          stroke-width: 2.6;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 28;
          stroke-dashoffset: 28;
        }
        .checklist-loader .cl-bar {
          position: relative;
          height: 11px;
          border-radius: 6px;
          background: var(--cl-bar-bg);
          overflow: hidden;
        }
        .checklist-loader .cl-shim {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 32%;
          background: var(--cl-shimmer);
        }

        .checklist-loader .r0 .cl-box { animation: cl-box0 var(--cl-cycle) infinite; }
        .checklist-loader .r1 .cl-box { animation: cl-box1 var(--cl-cycle) infinite; }
        .checklist-loader .r2 .cl-box { animation: cl-box2 var(--cl-cycle) infinite; }
        .checklist-loader .r0 .cl-chk path { animation: cl-draw0 var(--cl-cycle) infinite; }
        .checklist-loader .r1 .cl-chk path { animation: cl-draw1 var(--cl-cycle) infinite; }
        .checklist-loader .r2 .cl-chk path { animation: cl-draw2 var(--cl-cycle) infinite; }
        .checklist-loader .r0 .cl-ripple { animation: cl-rip0 var(--cl-cycle) infinite; }
        .checklist-loader .r1 .cl-ripple { animation: cl-rip1 var(--cl-cycle) infinite; }
        .checklist-loader .r2 .cl-ripple { animation: cl-rip2 var(--cl-cycle) infinite; }
        .checklist-loader .s0 { animation: cl-shim var(--cl-shimmer-time) linear infinite; }
        .checklist-loader .s1 { animation: cl-shim var(--cl-shimmer-time) 0.35s linear infinite; }
        .checklist-loader .s2 { animation: cl-shim var(--cl-shimmer-time) 0.7s linear infinite; }

        @keyframes cl-shim {
          0%   { transform: translateX(-160%) skewX(-18deg); }
          100% { transform: translateX(460%) skewX(-18deg); }
        }
        @keyframes cl-box0 {
          0%,6%    { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
          14%      { transform: scale(1.18); background: var(--cl-accent); border-color: var(--cl-accent); }
          19%,90%  { transform: scale(1);   background: var(--cl-accent);  border-color: var(--cl-accent); }
          93%,100% { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
        }
        @keyframes cl-box1 {
          0%,30%   { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
          38%      { transform: scale(1.18); background: var(--cl-accent); border-color: var(--cl-accent); }
          43%,90%  { transform: scale(1);   background: var(--cl-accent);  border-color: var(--cl-accent); }
          93%,100% { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
        }
        @keyframes cl-box2 {
          0%,54%   { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
          62%      { transform: scale(1.18); background: var(--cl-accent); border-color: var(--cl-accent); }
          67%,90%  { transform: scale(1);   background: var(--cl-accent);  border-color: var(--cl-accent); }
          93%,100% { transform: scale(0.5); background: transparent;       border-color: var(--cl-box-border); }
        }
        @keyframes cl-draw0 {
          0%,9%    { stroke-dashoffset: 28; }
          20%,90%  { stroke-dashoffset: 0; }
          93%,100% { stroke-dashoffset: 28; }
        }
        @keyframes cl-draw1 {
          0%,33%   { stroke-dashoffset: 28; }
          44%,90%  { stroke-dashoffset: 0; }
          93%,100% { stroke-dashoffset: 28; }
        }
        @keyframes cl-draw2 {
          0%,57%   { stroke-dashoffset: 28; }
          68%,90%  { stroke-dashoffset: 0; }
          93%,100% { stroke-dashoffset: 28; }
        }
        @keyframes cl-rip0 {
          0%,8%    { transform: scale(0.6);  opacity: 0; }
          13%      { transform: scale(0.85); opacity: 0.4; }
          28%,100% { transform: scale(2.2);  opacity: 0; }
        }
        @keyframes cl-rip1 {
          0%,32%   { transform: scale(0.6);  opacity: 0; }
          37%      { transform: scale(0.85); opacity: 0.4; }
          52%,100% { transform: scale(2.2);  opacity: 0; }
        }
        @keyframes cl-rip2 {
          0%,56%   { transform: scale(0.6);  opacity: 0; }
          61%      { transform: scale(0.85); opacity: 0.4; }
          76%,100% { transform: scale(2.2);  opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .checklist-loader * { animation: none !important; }
        }
      `}</style>
    </div>
  )
}
